import { useMemo, useRef, useState, useCallback } from "react";

export default function useProgress(
  initialInputs: {
    id: number;
    name: string;
    value:
      | string
      | number
      | { depart: string; return?: string | undefined }
      | { adult: number; child: (string | undefined)[] } // Add this type
      | undefined;
  }[],
  options: {
    visibleInputIds?: number[];
  } = {},
) {
  const [openStatus, setOpenStatus] = useState(
    initialInputs.map(({ id }) => ({ open: false, id })),
  );
  const [completedSequence, setCompletedSequence] = useState(0);
  const selectedSequenceRef = useRef<number[]>([]);
  const lastAutoAdvanceAtRef = useRef(0);

  const visibleInputIds = useMemo(() => {
    const visibleIds = options.visibleInputIds ?? initialInputs.map(({ id }) => id);

    return initialInputs
      .map(({ id }) => id)
      .filter((id) => visibleIds.includes(id));
  }, [initialInputs, options.visibleInputIds]);

  const handleProgress = useCallback(
    (id: number, isValueSet = false, nextOpen?: boolean) => {
      if (
        !isValueSet &&
        nextOpen === false &&
        Date.now() - lastAutoAdvanceAtRef.current < 350
      ) {
        return;
      }

      const currentVisibleIndex = visibleInputIds.indexOf(id);
      const nextVisibleInputId = visibleInputIds[currentVisibleIndex + 1];

      if (isValueSet) {
        lastAutoAdvanceAtRef.current = Date.now();
      }

      setOpenStatus((prev) =>
        isValueSet
          ? prev.map((item) => ({
              ...item,
              open: item.id === nextVisibleInputId,
            }))
          : prev.map((item) => ({
              ...item,
              open:
                item.id === id
                  ? nextOpen ?? !item.open
                  : false,
            })),
      );

      if (!isValueSet || currentVisibleIndex === -1) return;

      const firstInputId = visibleInputIds[0];
      const lastInputId = visibleInputIds[visibleInputIds.length - 1];
      const selectedSequence = selectedSequenceRef.current;
      const expectedNextInputId = visibleInputIds[selectedSequence.length];
      const nextSelectedSequence =
        id === firstInputId
          ? [id]
          : selectedSequence.length > 0 && id === expectedNextInputId
            ? [...selectedSequence, id]
            : [];

      selectedSequenceRef.current = nextSelectedSequence;

      if (
        visibleInputIds.length > 1 &&
        id === lastInputId &&
        nextSelectedSequence.length === visibleInputIds.length
      ) {
        setCompletedSequence((current) => current + 1);
        selectedSequenceRef.current = [];
      }
    },
    [visibleInputIds],
  );

  return {
    completedSequence,
    handleProgress,
    openStatus,
  };
}
